
import type { Route } from 'vue-router'
import type { AuditItem, DateFilterSelectedValues, GuardResolveFunction } from '../models'
import { defineComponent } from 'vue'

import { mapActions, mapGetters } from 'vuex'

import AuditEntry from '../components/audit/audit-entry.component.vue'
import { EpbButton } from '../components/shared/epb-button'
import SearchBox from '../components/shared/search-box.component.vue'
import TaggingBanner from '../components/tagging/tagging-banner.component.vue'

import {
  AuditSortFieldEnum,
  FilterIdEnum,
  FilterTypeEnum,
  PeriodEnum,
  SortDirectionEnum,
} from '../enums'

import { AUDIT_CONSTS } from '../store/audit/audit.consts'
import { CONTEXT_CONSTS } from '../store/context/context.consts'
import { TAGGING_CONSTS } from '../store/tagging/tagging.consts'

import { DateUtils } from '../utils'

interface AuditsGroup {
  id: string
  label: string
  items: AuditItem[]
}

export default defineComponent({
  name: 'Audit',
  components: {
    AuditEntry,
    EpbButton,
    SearchBox,
    TaggingBanner,
  },
  computed: {
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    }),
    ...mapGetters(TAGGING_CONSTS.moduleName, {
      taggingData: TAGGING_CONSTS.getters.data,
    }),
    ...mapGetters(AUDIT_CONSTS.moduleName, {
      data: AUDIT_CONSTS.getters.data,
    }),
    taggingBannerTitle(): string {
      const fromDate = new Date(this.period[0])
      fromDate.setUTCHours(12)
      const toDate = new Date(this.period[1])
      toDate.setUTCHours(12)
      toDate.setUTCDate(toDate.getUTCDate() - 1)
      const toLocaleStringOptions = { year: 'numeric', month: 'short', day: '2-digit' } as const
      const messages = this.$epb.messages.filters.values[FilterIdEnum.DATE]
      const fromDateMessage = this.$epb.utils.compileMessage(messages.from, {
        date: fromDate.toLocaleString(this.$epb.locale, toLocaleStringOptions),
      })
      const toDateMessage = this.$epb.utils.compileMessage(messages.to, {
        date: toDate.toLocaleString(this.$epb.locale, toLocaleStringOptions),
      })
      if (!!fromDate && !!toDate) {
        if (fromDate.getTime() === toDate.getTime()) {
          return messages.today.label
        }
        return `${fromDateMessage} ${toDateMessage}`
      }
      if (fromDate) {
        return fromDateMessage
      }
      if (toDate) {
        return toDateMessage
      }
      return ''
    },
    period(): DateFilterSelectedValues {
      let period = this.context.filters[FilterTypeEnum.DATE]
        .selectedValues as DateFilterSelectedValues
      if (period?.some((filter: number) => !filter)) {
        const today = new Date(Date.now())
        const startDate = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
        const endDate = Date.UTC(
          today.getUTCFullYear(),
          today.getUTCMonth(),
          today.getUTCDate() + 1,
        )
        period = [startDate, endDate]
      }
      return period
    },
    auditsGroups(): AuditsGroup[] {
      const dataValues = this.showLoadMoreButton ? this.data.slice(0, -1) : this.data
      return dataValues.reduce((auditsGroups: AuditsGroup[], audit: AuditItem) => {
        const auditGroupId = this.getAuditGroupId(audit)
        const auditsGroup = auditsGroups.find((group: AuditsGroup) => group.id === auditGroupId)
        if (!auditsGroup) {
          auditsGroups.push({
            id: auditGroupId,
            label: this.getAuditGroupLabel(audit),
            items: [audit],
          })
        } else {
          auditsGroup.items.push(audit)
        }
        return auditsGroups
      }, [])
    },
  },
  data() {
    return {
      pagination: {
        page: 1,
        itemsPerPage: 10,
      },
      sort: {
        field: AuditSortFieldEnum.DATE,
        direction: SortDirectionEnum.DESCENDING,
      },
      expandedAuditId: undefined,
      contextWatchDisabled: false,
      showLoadMoreButton: true,
      messages: this.$epb.messages.audit,
      strictMatching: true,
      loadingData: false,
    }
  },
  methods: {
    ...mapActions(CONTEXT_CONSTS.moduleName, {
      setQuery: CONTEXT_CONSTS.actions.setQuery,
      setContextStrictMatching: CONTEXT_CONSTS.actions.setStrictMatching,
    }),
    ...mapActions(AUDIT_CONSTS.moduleName, {
      read: AUDIT_CONSTS.actions.read,
    }),
    ...mapActions(TAGGING_CONSTS.moduleName, {
      loadViews: TAGGING_CONSTS.actions.loadViews,
      loadActions: TAGGING_CONSTS.actions.loadActions,
    }),
    async onQueryChange(newQuery: string): Promise<void> {
      await this.setQuery(newQuery || undefined)
      this.$emit('contextChanged')
    },
    onStrictMatchingChange() {
      this.strictMatching = !this.strictMatching
      this.contextWatchDisabled = !this.context?.query?.length

      this.setContextStrictMatching(this.strictMatching)
      this.$emit('contextChanged')
    },
    getAuditGroupId(audit: AuditItem): string {
      const auditGroupValue = audit[this.sort.field]
      if (this.sort.field === AuditSortFieldEnum.DATE) {
        const auditGroupDate = new Date(auditGroupValue)
        auditGroupDate.setUTCHours(0, 0, 0, 0)
        return auditGroupDate.toISOString()
      }
      return auditGroupValue
    },
    getAuditGroupLabel(audit: AuditItem): string {
      const auditGroupValue = audit[this.sort.field]
      if (this.sort.field === AuditSortFieldEnum.DATE) {
        return this.getDateLabel(auditGroupValue)
      }
      return auditGroupValue
    },
    getDateLabel(date: Date): string {
      const dateGroupsMessages = this.messages.sort[AuditSortFieldEnum.DATE].groups
      const today = new Date()
      const yesterday = new Date(new Date().setDate(today.getDate() - 1))
      if (DateUtils.isSamePeriod(PeriodEnum.DAY, date, today)) {
        return dateGroupsMessages.today
      } else if (DateUtils.isSamePeriod(PeriodEnum.DAY, date, yesterday)) {
        return dateGroupsMessages.yesterday
      } else {
        return date.toLocaleString(this.$epb.locale, {
          year: 'numeric',
          month: 'long',
          weekday: 'short',
          day: 'numeric',
        })
      }
    },
    onToggleAuditExpand(auditId: string): void {
      this.expandedAuditId = this.expandedAuditId !== auditId ? auditId : undefined
    },
    async loadData(resetPagination = false, loadTaggingData = false): Promise<void> {
      if (resetPagination) {
        this.pagination.page = 1
      }
      this.loadingData = true
      this.showLoadMoreButton = true
      try {
        await Promise.all([
          this.loadAuditData(),
          ...(loadTaggingData ? [this.loadViews(this.period), this.loadActions(this.period)] : []),
        ])
        if (loadTaggingData) {
          this.$refs.taggingBanner.clear()
        }
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.loadingData = false
      }
    },
    async loadAuditData(): Promise<void> {
      await this.read({
        pagination: this.pagination,
        strictMatching: this.strictMatching,
      })
      this.showLoadMoreButton =
        this.data.length === this.pagination.itemsPerPage * this.pagination.page + 1
    },
    onLoadMoreClick(): void {
      this.pagination.page++
      this.loadData()
    },
  },
  watch: {
    context: {
      async handler(): Promise<void> {
        if (!this.contextWatchDisabled) {
          await this.loadData(true, true)
        }
      },
      deep: true,
      immediate: true,
    },
  },
  async beforeRouteLeave(to: Route, from: Route, next: GuardResolveFunction): Promise<void> {
    this.contextWatchDisabled = true

    next()
  },
  created() {
    if (typeof this.context.strictMatching === 'boolean') {
      this.strictMatching = this.context.strictMatching
    }
  },
})
